// VMT Reduction Component Types

export interface VmtReductionComponents {
  transitOrientedDevelopment: number; // 0-15%
  activeTransportation: number; // 0-10%
  teleworkPrograms: number; // 0-15%
  transitImprovements: number; // 0-10%
}

export interface ParkingPricingAssumptions {
  priceIncreasePercent: number; // 10-300% (main lever)
  vmtAffectedPercent: number; // Percentage of total VMT that can be affected (default 25%)
  startingPricePerHour: number; // Starting parking price per hour (default $0)
  commuteByCarPercent: number; // Percentage who commute by car alone (fetched from database)
}

export interface InterventionAreas {
  // Transportation (Light Blue)
  urbanInfill: number; // 0-100% by 2045
  vmtReduction: number; // 0-50% by 2045
  vehicleElectrification: number; // 0-100% by 2045
  // Buildings (Yellow)
  energyEfficiency: number; // 0-100% by 2045
  buildingsElectrification: number; // 0-100% by 2045
  gridDecarbonization: number; // 0-100% by 2045
  // Other (Orange)
  organicsDiversion: number; // 0-100% by 2045
}

export interface VmtComponentMetadata {
  key: keyof VmtReductionComponents;
  label: string;
  description: string;
  maxPercent: number;
  color: string;
  literatureSource: string;
}

// Component metadata with colors (shades of green to match existing VMT green #10b981)
export const VMT_COMPONENT_METADATA: VmtComponentMetadata[] = [
  {
    key: 'transitOrientedDevelopment',
    label: 'Transit-Oriented Development',
    description: 'Increase housing density near transit, mixed-use development, and walkable neighborhoods',
    maxPercent: 15,
    color: '#059669', // Green-600
    literatureSource: 'National Academies (5-15%), Austin TOD Study (21-27%)',
  },
  {
    key: 'activeTransportation',
    label: 'Active Transportation Infrastructure',
    description: 'Build protected bike lanes, pedestrian infrastructure, and complete streets',
    maxPercent: 10,
    color: '#10b981', // Green-500 (main VMT color)
    literatureSource: 'CARB Technical Guidance on Bicycle Facilities',
  },
  {
    key: 'teleworkPrograms',
    label: 'Telework & Remote Work',
    description: 'Promote telework policies, remote work programs, and flexible schedules',
    maxPercent: 15,
    color: '#34d399', // Green-400
    literatureSource: 'Telework reduces 60-90% of commute VMT (Fehr & Peers, CalTrans)',
  },
  {
    key: 'transitImprovements',
    label: 'Transit Service Improvements',
    description: 'Expand transit frequency, coverage, and provide fare subsidies',
    maxPercent: 10,
    color: '#6ee7b7', // Green-300
    literatureSource: 'Transit proximity: 25-50% VMT reduction',
  },
];

// Default distribution ratios (used when scaling from total slider)
export const DEFAULT_VMT_DISTRIBUTION = {
  transitOrientedDevelopment: 0.35, // 35% of total
  activeTransportation: 0.15, // 15% of total
  teleworkPrograms: 0.35, // 35% of total
  transitImprovements: 0.15, // 15% of total
};

// Helper function to get default (zero) components
export function getDefaultVmtComponents(): VmtReductionComponents {
  return {
    transitOrientedDevelopment: 0,
    activeTransportation: 0,
    teleworkPrograms: 0,
    transitImprovements: 0,
  };
}

// Helper function to get default (zero) intervention areas
export function getDefaultInterventionAreas(): InterventionAreas {
  return {
    urbanInfill: 0,
    vmtReduction: 0,
    vehicleElectrification: 0,
    energyEfficiency: 0,
    buildingsElectrification: 0,
    gridDecarbonization: 0,
    organicsDiversion: 0,
  };
}

// Calculate total VMT reduction from components
export function calculateTotalVmtReduction(components: VmtReductionComponents): number {
  return (
    components.transitOrientedDevelopment +
    components.activeTransportation +
    components.teleworkPrograms +
    components.transitImprovements
  );
}

// Distribute total VMT reduction across components proportionally
export function distributeVmtReduction(
  totalPercent: number,
  currentComponents: VmtReductionComponents
): VmtReductionComponents {
  const currentTotal = calculateTotalVmtReduction(currentComponents);

  if (currentTotal === 0) {
    // Use default distribution based on literature-supported ratios
    return {
      transitOrientedDevelopment: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.transitOrientedDevelopment, 15),
      activeTransportation: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.activeTransportation, 10),
      teleworkPrograms: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.teleworkPrograms, 15),
      transitImprovements: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.transitImprovements, 10),
    };
  }

  // Proportional scaling
  const scaleFactor = totalPercent / currentTotal;
  return {
    transitOrientedDevelopment: Math.min(currentComponents.transitOrientedDevelopment * scaleFactor, 15),
    activeTransportation: Math.min(currentComponents.activeTransportation * scaleFactor, 10),
    teleworkPrograms: Math.min(currentComponents.teleworkPrograms * scaleFactor, 15),
    transitImprovements: Math.min(currentComponents.transitImprovements * scaleFactor, 10),
  };
}

// Validate VMT components
export function validateVmtComponents(
  components: VmtReductionComponents
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (components.transitOrientedDevelopment > 15) {
    errors.push('TOD exceeds max (15%)');
  }
  if (components.activeTransportation > 10) {
    errors.push('Active transport exceeds max (10%)');
  }
  if (components.teleworkPrograms > 15) {
    errors.push('Telework exceeds max (15%)');
  }
  if (components.transitImprovements > 10) {
    errors.push('Transit improvements exceeds max (10%)');
  }

  const total = calculateTotalVmtReduction(components);
  if (total > 50) {
    errors.push('Total VMT reduction exceeds 50%');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// Micromobility assumptions used by the Policy Impact Tool
export interface MicromobilityAssumptions {
  yearImplemented: number; // default 2025
  tripsPerResident: number; // e.g., 3 trips per resident per year
  percentBike: number; // percent of program trips by e-bike (0-100)
  percentScooter: number; // percent of program trips by e-scooter (0-100)
  milesPerBikeTrip: number; // average miles per e-bike trip
  milesPerScooterTrip: number; // average miles per e-scooter trip
  percentBikeTripsDisplaceCars: number; // 0-100
  percentScooterTripsDisplaceCars: number; // 0-100
  kwhPerMileBike: number; // EV bike electricity consumption (kWh/mile)
  kwhPerMileScooter: number; // EV scooter electricity consumption (kWh/mile)
  scalePercent: number; // user scale control (0-200%)
}
